import { hash01, particleSeed } from '../utils/deterministic';
import type { DustParticle, EmberParticle, LensDrop, RainParticle, SnowParticle } from './types';

const MAX_RAIN = 2200;
const MAX_SNOW = 1400;
const MAX_EMBERS = 900;
const MAX_DUST = 700;
const MAX_DROPS = 80;

/** Stable per-effect seed — particle identity must not change every frame. */
function effectBaseSeed(effectId: string, seed: number): number {
  return particleSeed(effectId, 0, seed);
}

function motionTime(frameIndex: number, speed: number, scale: number): number {
  if (!(speed > 0)) return 0;
  return frameIndex * speed * scale;
}

export function buildRainParticles(
  w: number,
  h: number,
  density: number,
  speed: number,
  wind: number,
  dropSize: number,
  frameIndex: number,
  seed: number,
): RainParticle[] {
  const count = Math.min(MAX_RAIN, Math.round(w * h * 0.00028 * density));
  const particles: RainParticle[] = [];
  const base = effectBaseSeed('rain', seed);
  const t = motionTime(frameIndex, speed, 0.09);
  for (let i = 0; i < count; i += 1) {
    const spawnX = hash01(i, 0, base) * w;
    const spawnY = hash01(i, 1, base) * h;
    const depth = hash01(i, 5, base);
    const depthScale = 0.55 + depth * 0.9;
    const x = spawnX + wind * t * (28 + depth * 28);
    const y = ((spawnY + t * h * (0.28 + depth * 0.22)) % (h + 50)) - 25;
    particles.push({
      x: ((x % w) + w) % w,
      y,
      velocity: speed > 0 ? (10 + hash01(i, 2, base) * 18) * depthScale * Math.max(speed, 0.15) : 0,
      size: (0.35 + hash01(i, 3, base) * 1.8) * dropSize * depthScale,
      opacity: (0.12 + hash01(i, 4, base) * 0.55) * (0.55 + depth * 0.45),
    });
  }
  return particles;
}

export function buildSnowParticles(
  w: number,
  h: number,
  density: number,
  wind: number,
  flakeSize: number,
  frameIndex: number,
  seed: number,
): SnowParticle[] {
  const count = Math.min(MAX_SNOW, Math.round(w * h * 0.00014 * density));
  const particles: SnowParticle[] = [];
  const base = effectBaseSeed('snow', seed);
  for (let i = 0; i < count; i += 1) {
    const layer = Math.floor(hash01(i, 5, base) * 4);
    const layerSpeed = 0.1 + layer * 0.1;
    const t = frameIndex * layerSpeed;
    const spawnX = hash01(i, 0, base) * w;
    const spawnY = hash01(i, 1, base) * h;
    const x = spawnX + Math.sin(t * 0.045 + i * 0.31) * wind * (22 + layer * 10);
    const y = ((spawnY + t * h * 0.06 * (1 + layer * 0.28)) % (h + 24)) - 12;
    particles.push({
      x: ((x % w) + w) % w,
      y,
      size: (0.9 + hash01(i, 2, base) * 3.4) * flakeSize * (1 + layer * 0.18),
      opacity: 0.25 + hash01(i, 3, base) * 0.55,
      layer,
      drift: Math.sin(t * 0.09 + i * 0.7) * wind * (1.5 + layer * 0.4),
    });
  }
  return particles;
}

export function buildEmberParticles(
  w: number,
  h: number,
  count: number,
  brightness: number,
  speed: number,
  frameIndex: number,
  seed: number,
): EmberParticle[] {
  const n = Math.min(MAX_EMBERS, Math.round(count));
  const particles: EmberParticle[] = [];
  const base = effectBaseSeed('embers', seed);
  const t = motionTime(frameIndex, speed, 0.05);
  for (let i = 0; i < n; i += 1) {
    const life = hash01(i, 0, base);
    const spawnX = hash01(i, 1, base) * w;
    const drift = speed > 0 ? Math.sin(t + i * 0.37) * 10 * speed : 0;
    const x = spawnX + drift;
    const cycle = h * (0.85 + life * 0.3);
    const rise = speed > 0
      ? ((life * h * 0.25) + t * h * 0.22) % cycle
      : life * h * 0.25;
    const y = h - rise;
    particles.push({
      x,
      y,
      vx: (hash01(i, 2, base) - 0.5) * 2.4 * Math.max(speed, 0),
      vy: speed > 0 ? -2.2 - hash01(i, 3, base) * 4.5 * speed : 0,
      size: 1.1 + hash01(i, 4, base) * 3.6,
      brightness: brightness * (0.45 + hash01(i, 5, base) * 0.55),
      life,
    });
  }
  return particles;
}

export function buildDustParticles(
  w: number,
  h: number,
  density: number,
  speed: number,
  frameIndex: number,
  seed: number,
): DustParticle[] {
  const count = Math.min(MAX_DUST, Math.round(w * h * 0.00007 * density));
  const particles: DustParticle[] = [];
  const base = effectBaseSeed('dust', seed);
  const t = motionTime(frameIndex, speed, 0.02);
  for (let i = 0; i < count; i += 1) {
    const depth = hash01(i, 0, base);
    const spawnX = hash01(i, 1, base) * w;
    const spawnY = hash01(i, 2, base) * h;
    const depthScale = 0.5 + depth;
    particles.push({
      x: (spawnX + Math.sin(t * depthScale + i) * 14 * Math.max(speed, 0)) % w,
      y: (spawnY + Math.cos(t * 0.7 * depthScale + i) * 9 * Math.max(speed, 0)) % h,
      size: (0.6 + depth * 2.4) * (1.55 - depth * 0.45),
      opacity: 0.06 + depth * 0.28,
      depth,
      driftX: Math.sin(t * 0.3 + i) * speed,
      driftY: Math.cos(t * 0.2 + i) * speed * 0.5,
    });
  }
  return particles;
}

export function buildLensDrops(
  w: number,
  h: number,
  count: number,
  size: number,
  refraction: number,
  frameIndex: number,
  seed: number,
): LensDrop[] {
  const n = Math.min(MAX_DROPS, Math.round(count));
  const drops: LensDrop[] = [];
  const base = effectBaseSeed('drops', seed);
  for (let i = 0; i < n; i += 1) {
    const phase = frameIndex * 0.018 + hash01(i, 0, base) * Math.PI * 2;
    drops.push({
      x: hash01(i, 1, base) * w,
      y: hash01(i, 2, base) * h,
      radius: (10 + hash01(i, 3, base) * 28) * size,
      refraction,
      phase,
    });
  }
  return drops;
}

export function drawRain(ctx: CanvasRenderingContext2D, particles: RainParticle[], wind: number): void {
  ctx.lineCap = 'round';
  for (let i = 0; i < particles.length; i += 1) {
    const p = particles[i];
    const dx = wind * (5 + p.size * 2);
    const dy = Math.max(6, p.velocity);
    const x2 = p.x + dx;
    const y2 = p.y + dy;

    // Soft glow trail
    ctx.globalAlpha = p.opacity * 0.35;
    ctx.strokeStyle = 'rgba(170,205,255,0.9)';
    ctx.lineWidth = p.size * 2.4;
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    ctx.lineTo(x2, y2);
    ctx.stroke();

    // Crisp core streak
    ctx.globalAlpha = Math.min(1, p.opacity * 1.15);
    ctx.strokeStyle = 'rgba(220,235,255,0.95)';
    ctx.lineWidth = Math.max(0.6, p.size * 0.85);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    ctx.lineTo(x2, y2);
    ctx.stroke();

    // Tip highlight
    ctx.globalAlpha = p.opacity * 0.7;
    ctx.fillStyle = 'rgba(240,248,255,0.9)';
    ctx.beginPath();
    ctx.arc(x2, y2, Math.max(0.5, p.size * 0.35), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

export function drawSnow(ctx: CanvasRenderingContext2D, particles: SnowParticle[]): void {
  for (let i = 0; i < particles.length; i += 1) {
    const p = particles[i];
    const cx = p.x + p.drift;
    const cy = p.y;
    const r = p.size;

    // Soft halo
    const halo = ctx.createRadialGradient(cx, cy, 0, cx, cy, r * 2.2);
    halo.addColorStop(0, `rgba(255,255,255,${p.opacity * 0.85})`);
    halo.addColorStop(0.45, `rgba(230,240,255,${p.opacity * 0.35})`);
    halo.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(cx, cy, r * 2.2, 0, Math.PI * 2);
    ctx.fill();

    // Crisp flake core
    ctx.globalAlpha = Math.min(1, p.opacity + 0.1);
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(cx, cy, Math.max(0.6, r * 0.55), 0, Math.PI * 2);
    ctx.fill();

    // Cross arms for larger flakes
    if (r > 2.2) {
      ctx.strokeStyle = `rgba(255,255,255,${p.opacity * 0.75})`;
      ctx.lineWidth = Math.max(0.5, r * 0.18);
      ctx.beginPath();
      ctx.moveTo(cx - r, cy);
      ctx.lineTo(cx + r, cy);
      ctx.moveTo(cx, cy - r);
      ctx.lineTo(cx, cy + r);
      ctx.moveTo(cx - r * 0.7, cy - r * 0.7);
      ctx.lineTo(cx + r * 0.7, cy + r * 0.7);
      ctx.moveTo(cx + r * 0.7, cy - r * 0.7);
      ctx.lineTo(cx - r * 0.7, cy + r * 0.7);
      ctx.stroke();
    }
  }
  ctx.globalAlpha = 1;
}

export function drawEmbers(ctx: CanvasRenderingContext2D, particles: EmberParticle[]): void {
  for (let i = 0; i < particles.length; i += 1) {
    const p = particles[i];
    const trailX = p.x - p.vx * 3;
    const trailY = p.y - p.vy * 2.5;

    // Motion trail
    const trail = ctx.createLinearGradient(trailX, trailY, p.x, p.y);
    trail.addColorStop(0, 'rgba(255,80,20,0)');
    trail.addColorStop(1, `rgba(255,${Math.round(140 + 80 * p.brightness)},40,${0.45 * p.brightness})`);
    ctx.strokeStyle = trail;
    ctx.lineWidth = Math.max(1, p.size * 0.7);
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(trailX, trailY);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();

    const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 3.2);
    g.addColorStop(0, `rgba(255,255,220,${Math.min(1, p.brightness)})`);
    g.addColorStop(0.25, `rgba(255,${Math.round(170 * p.brightness)},60,${0.85 * p.brightness})`);
    g.addColorStop(0.65, `rgba(255,80,10,${0.35 * p.brightness})`);
    g.addColorStop(1, 'rgba(255,40,0,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.size * 3.2, 0, Math.PI * 2);
    ctx.fill();
  }
}

export function drawDust(ctx: CanvasRenderingContext2D, particles: DustParticle[]): void {
  for (let i = 0; i < particles.length; i += 1) {
    const p = particles[i];
    const cx = p.x + p.driftX;
    const cy = p.y + p.driftY;
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, p.size * 2.5);
    g.addColorStop(0, `rgba(255,248,230,${p.opacity * 1.2})`);
    g.addColorStop(0.5, `rgba(255,236,200,${p.opacity * 0.45})`);
    g.addColorStop(1, 'rgba(255,240,210,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, cy, p.size * 2.5, 0, Math.PI * 2);
    ctx.fill();
  }
}

export function drawLensDrops(
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  drops: LensDrop[],
  w: number,
  h: number,
): void {
  for (let i = 0; i < drops.length; i += 1) {
    const d = drops[i];
    const wobble = Math.sin(d.phase) * 2.5;
    const cx = d.x + wobble;
    const cy = d.y + wobble * 0.45;
    const r = d.radius;
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.clip();
    const off = d.refraction * 5;
    ctx.drawImage(source, cx - r - off, cy - r - off, r * 2 + off * 2, r * 2 + off * 2, cx - r, cy - r, r * 2, r * 2);
    ctx.restore();

    // Specular rim
    const rim = ctx.createRadialGradient(cx - r * 0.25, cy - r * 0.35, r * 0.1, cx, cy, r);
    rim.addColorStop(0, 'rgba(255,255,255,0.55)');
    rim.addColorStop(0.35, 'rgba(255,255,255,0.12)');
    rim.addColorStop(0.75, 'rgba(180,210,255,0.08)');
    rim.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = rim;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = 'rgba(255,255,255,0.35)';
    ctx.lineWidth = Math.max(1, r * 0.06);
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.96, 0, Math.PI * 2);
    ctx.stroke();
  }
}
